/* eslint-disable max-classes-per-file */
/* eslint-disable @typescript-eslint/naming-convention */
import { AnalyticsEvent } from '@shure/shared/models';

import { CdmAnalyticsEvents } from './cdm-analytics-events.enum';
import { AnalyticsCommonDeviceInfo, AnalyticsLocation } from './common.model';

export type FirmwareUpdateDialogLaunchedLocation =
	| AnalyticsLocation.PropertyPanel
	| AnalyticsLocation.DevicesTable
	| AnalyticsLocation.UpdateSelectedButton
	| AnalyticsLocation.StartJobNowButton;

export type FirmwareUpdateRemoveLocation =
	| AnalyticsLocation.FirmwareUpdateDialogTable
	| AnalyticsLocation.CreateJobTable
	| AnalyticsLocation.JobDetailsTable;

export type FirmwareUpdateSelectionChangeLocation =
	| AnalyticsLocation.DevicesTable
	| AnalyticsLocation.PropertyPanel
	| AnalyticsLocation.FirmwareUpdateDialogTable
	| AnalyticsLocation.CreateJobTable
	| AnalyticsLocation.JobDetailsTable;

export interface FirmwareUpdateAnalyticsInfo {
	package_version?: string;
	package_key?: string;
	hosted: boolean;
}

export interface FirmwareUpdateDeviceInfo {
	device: AnalyticsCommonDeviceInfo;
	old_firmware_package_version: string;
	new_firmware_package_version: string;
	new_firmware_package_key: string;
	hosted: boolean;
}

/**
 * Event to measure how a user interacts with the FW Update dialog,
 * such as where it is normally launched from and how many devices are selected at the time when the dialog is launched.
 */
export class FirmwareUpdateDialogLaunchedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.FirmwareUpdateDialogLaunched;
	public readonly properties: {
		location: FirmwareUpdateDialogLaunchedLocation;
		device_count: number;
	};

	constructor(location: FirmwareUpdateDialogLaunchedLocation, deviceCount: number) {
		this.properties = {
			location: location,
			device_count: deviceCount
		};
	}
}

/**
 * Event to measure how often a user closes the FW Update dialog w/out updating firmware
 */
export class FirmwareUpdateDialogCancelledEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.FirmwareUpdateDialogCancelled;
	public readonly properties = {};
}

/**
 * Event to measure how often a user initiates a firmware update and the accompanying device information.
 */
export class FirmwareUpdateSubmittedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.FirmwareUpdateSubmitted;
	public readonly properties: {
		source_location: FirmwareUpdateDialogLaunchedLocation;
		devices: FirmwareUpdateDeviceInfo[];
	};

	constructor(location: FirmwareUpdateDialogLaunchedLocation, devices: FirmwareUpdateDeviceInfo[]) {
		this.properties = {
			source_location: location,
			devices: devices
		};
	}
}

/**
 * Event to measure how often devices are removed from a scheduled firmware update, and the accompanying device information.
 */
export class FirmwareUpdateRemovedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.FirmwareUpdateRemoved;
	public readonly properties: {
		location: FirmwareUpdateRemoveLocation;
		device: AnalyticsCommonDeviceInfo;
		source_location?: FirmwareUpdateDialogLaunchedLocation;
	};

	constructor(
		location: FirmwareUpdateRemoveLocation,
		device: AnalyticsCommonDeviceInfo,
		dialogLaunchLocation?: FirmwareUpdateDialogLaunchedLocation
	) {
		this.properties = {
			location: location,
			device: device,
			source_location: dialogLaunchLocation
		};
	}
}

export class DefaultFirmwareSelectionChangedEvent implements AnalyticsEvent {
	public readonly event = CdmAnalyticsEvents.DefaultFirmwareSelectionChanged;
	public readonly properties: {
		devices: AnalyticsCommonDeviceInfo[];
		location: FirmwareUpdateSelectionChangeLocation;
		old_firmware: FirmwareUpdateAnalyticsInfo;
		new_firmware: FirmwareUpdateAnalyticsInfo;
	};

	constructor(
		devices: AnalyticsCommonDeviceInfo[],
		location: FirmwareUpdateSelectionChangeLocation,
		oldFirmware: FirmwareUpdateAnalyticsInfo,
		newFirmware: FirmwareUpdateAnalyticsInfo
	) {
		this.properties = {
			devices: devices,
			location: location,
			old_firmware: oldFirmware,
			new_firmware: newFirmware
		};
	}
}
